import { Link } from 'react-router-dom';
import Button from '../base/Button';
import { useLanguage } from '@/localization';

export type Tier = 'vip' | 'vip_plus';

export const TIER_ORDER: Tier[] = ['vip', 'vip_plus'];

export interface TierStyle {
  multiplier: string;
  icon: string;
  tagline: string;
  popular: boolean;
  textColor: string;
  accentGradient: string;
  iconGradient: string;
  borderColor: string;
  hoverBorder: string;
  selectedBorder: string;
  glowGradient: string;
  hoverShadow: string;
  multiplierColor: string;
  multiplierGlow: string;
  cardBg: string;
  featureIconBg: string;
  featureIconColor: string;
  dividerVia: string;
  ctaVariant: 'primary' | 'outline';
  ctaPulse: boolean;
  features: string[];
}

export const TIER_DISPLAY: Record<Tier, TierStyle> = {
  vip: {
    multiplier: '2x',
    icon: 'ri-vip-crown-2-fill',
    tagline: 'Enhanced Experience',
    popular: false,
    textColor: 'text-amber-400',
    accentGradient: 'from-amber-700 via-yellow-500 to-amber-700',
    iconGradient: 'from-amber-500 to-amber-800',
    borderColor: 'border-amber-900/20',
    hoverBorder: 'group-hover:border-amber-500/40',
    selectedBorder: 'border-amber-500/40',
    glowGradient: 'from-amber-500/15 via-amber-600/5 to-transparent',
    hoverShadow: 'group-hover:shadow-amber-900/20',
    multiplierColor: 'text-amber-400',
    multiplierGlow: 'drop-shadow-[0_0_24px_rgba(245,158,11,0.25)]',
    cardBg: 'bg-[radial-gradient(ellipse_at_top,rgba(245,158,11,0.04),transparent_55%)]',
    featureIconBg: 'bg-amber-500/10',
    featureIconColor: 'text-amber-400',
    dividerVia: 'via-amber-500/20',
    ctaVariant: 'outline',
    ctaPulse: false,
    features: [
      'Double Experience (2x XP)',
      'Custom Skins',
      'Custom Weapons',
      'Custom Boosts',
      'VIP Tag on Scoreboard',
      'Green Nickname Color',
      'Extended AFK Timer (15 min)',
    ],
  },
  vip_plus: {
    multiplier: '3x',
    icon: 'ri-skull-2-fill',
    tagline: 'Ultimate Power',
    popular: true,
    textColor: 'text-red-400',
    accentGradient: 'from-red-700 via-red-500 to-red-700',
    iconGradient: 'from-red-500 to-red-800',
    borderColor: 'border-red-800/30',
    hoverBorder: 'group-hover:border-red-500/50',
    selectedBorder: 'border-red-500/50',
    glowGradient: 'from-red-500/20 via-red-600/8 to-transparent',
    hoverShadow: 'group-hover:shadow-red-900/30',
    multiplierColor: 'text-red-400',
    multiplierGlow: 'drop-shadow-[0_0_30px_rgba(220,38,38,0.35)]',
    cardBg: 'bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.07),transparent_55%)]',
    featureIconBg: 'bg-red-500/10',
    featureIconColor: 'text-red-400',
    dividerVia: 'via-red-500/25',
    ctaVariant: 'primary',
    ctaPulse: true,
    features: [
      'Triple Experience (3x XP)',
      'All VIP Benefits Included',
      'Exclusive VIP+ Skins',
      'Exclusive VIP+ Weapons',
      'Custom Boosts',
      'Red VIP+ Tag on Scoreboard',
      'Red Nickname Color',
      'Extended AFK Timer (30 min)',
    ],
  },
};

interface VipTierCardProps {
  tier: Tier;
  name: string;
  monthlyPrice: number | null;
  selected?: boolean;
  glowPersistent?: boolean;
  onClick?: () => void;
  ctaHref?: string;
}

export default function VipTierCard({
  tier,
  name,
  monthlyPrice,
  selected = false,
  glowPersistent = false,
  onClick,
  ctaHref,
}: VipTierCardProps) {
  const { t } = useLanguage();
  const s = TIER_DISPLAY[tier];
  const glowVisible = glowPersistent || selected;

  return (
    <div
      className={`relative group h-full ${onClick ? 'cursor-pointer' : ''}`}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      {/* Glow border */}
      <div
        className={`absolute -inset-px rounded-2xl bg-gradient-to-b ${s.glowGradient} blur-[1px] transition-opacity duration-500 ${
          glowVisible ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
      />

      {/* Card body */}
      <div
        className={`relative h-full flex flex-col rounded-2xl border ${
          selected ? s.selectedBorder : s.borderColor
        } ${s.hoverBorder} ${s.cardBg} backdrop-blur-sm overflow-hidden transition-all duration-500 group-hover:-translate-y-1 group-hover:shadow-2xl ${s.hoverShadow}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
      >
        {/* Top accent gradient bar */}
        <div className={`h-[3px] bg-gradient-to-r ${s.accentGradient}`} />

        {/* Popular badge */}
        {s.popular && (
          <div className="absolute top-5 right-5 z-10">
            <span className="inline-flex items-center gap-1.5 bg-red-600/90 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow-lg shadow-red-600/25 backdrop-blur-sm">
              <i className="ri-fire-fill text-[11px]" />
              {t('MOST POPULAR')}
            </span>
          </div>
        )}

        <div className="flex flex-col flex-1 p-8 pt-7">
          {/* Tier header: icon + name */}
          <div className="flex items-center gap-4 mb-6">
            <div
              className={`w-14 h-14 rounded-xl bg-gradient-to-br ${s.iconGradient} flex items-center justify-center shadow-lg shrink-0`}
            >
              <i className={`${s.icon} text-white text-2xl`} />
            </div>
            <div className="min-w-0">
              <h3 className="text-2xl font-bold text-white font-orbitron leading-tight">
                {name}
              </h3>
              <p className="text-sm text-gray-400 font-rajdhani mt-0.5">
                {t(s.tagline)}
              </p>
            </div>
          </div>

          {/* XP multiplier */}
          <div className="text-center py-5 mb-2">
            <span
              className={`text-7xl font-black font-orbitron ${s.multiplierColor} ${s.multiplierGlow} leading-none tracking-tight`}
            >
              {s.multiplier}
            </span>
            <div className="text-[10px] font-bold uppercase tracking-[0.35em] text-gray-500 mt-2">
              {t('XP Boost')}
            </div>
          </div>

          {/* Price */}
          <div className="text-center mb-7">
            <div className="flex items-baseline justify-center gap-1">
              <span className="text-4xl font-bold text-white font-orbitron">
                {monthlyPrice == null
                  ? '—'
                  : `€${monthlyPrice.toFixed(2)}`}
              </span>
              {monthlyPrice != null && (
                <span className="text-gray-400 text-lg font-rajdhani">
                  /{t('month')}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1.5">
              {t('Recurring subscription — cancel anytime')}
            </p>
          </div>

          {/* Divider with label */}
          <div className="flex items-center gap-3 mb-5">
            <div
              className={`flex-1 h-px bg-gradient-to-r from-transparent ${s.dividerVia} to-transparent`}
            />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              {t('Perks')}
            </span>
            <div
              className={`flex-1 h-px bg-gradient-to-r from-transparent ${s.dividerVia} to-transparent`}
            />
          </div>

          {/* Feature list */}
          <ul className="space-y-2.5 mb-8 flex-1">
            {s.features.map((feature) => (
              <li
                key={feature}
                className="flex items-center gap-3 text-gray-300 group/feat hover:text-white transition-colors duration-200"
              >
                <span
                  className={`w-5 h-5 rounded ${s.featureIconBg} flex items-center justify-center shrink-0 transition-transform duration-200 group-hover/feat:scale-110`}
                >
                  <i
                    className={`ri-check-line ${s.featureIconColor} text-[10px]`}
                  />
                </span>
                <span className="text-sm leading-snug">{t(feature)}</span>
              </li>
            ))}
          </ul>

          {/* CTA — only rendered when ctaHref is provided (home page) */}
          {ctaHref && (
            <Link to={ctaHref} className="block mt-auto">
              <Button
                variant={s.ctaVariant}
                size="lg"
                className={`w-full ${s.ctaPulse ? 'pulse-glow' : ''}`}
              >
                {tier === 'vip_plus'
                  ? t('Get VIP+ Now')
                  : t('Get VIP Now')}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
